import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { retryJobStep } from '@/api/generated/jobs/jobs';
import { Badge, Button } from '@/shared/ui';
import { cn } from '@/shared/lib/utils';
import { JobSegmentedBar } from '../../internal/JobSegmentedBar';
import { JobStatusIcon } from '../../internal/JobStatusIcon';
import { formatDuration, formatThousands } from '../../internal/jobsFormat';
import type { JobTreeGroupRow, JobTreeRow as JobTreeRowVm } from '../../internal/jobsModel';

interface JobTreeRowProps {
  row: JobTreeRowVm;
  runId: string;
  now: number;
  onToggle: (group: JobTreeGroupRow) => void;
}

/**
 * Jeden řádek stromu kroků (dle designu): vodicí linky předků + elbow konektor; skupiny se
 * sbalováním, badge „max. N souběžně", mini-barem a počtem done/total; listy s poznámkou čekání,
 * chybou, progress barem, dobou běhu a tlačítkem „Opakovat" u chyb.
 */
export function JobTreeRow({ row, runId, now, onToggle }: JobTreeRowProps) {
  const { t } = useTranslation('jobs');
  const [retrying, setRetrying] = useState(false);

  const guides = (
    <>
      {row.guides.map((visible, index) => (
        <span key={index} className="relative shrink-0 basis-[9px] self-stretch">
          {visible ? (
            <span className="bg-phx-border-2 absolute top-0 bottom-0 left-[4px] w-px" aria-hidden />
          ) : null}
        </span>
      ))}
      {row.hasConnector ? (
        <span className="relative shrink-0 basis-[9px] self-stretch" aria-hidden>
          <span className="bg-phx-border-2 absolute top-0 left-[4px] h-1/2 w-px" />
          {row.connectorContinues ? (
            <span className="bg-phx-border-2 absolute top-1/2 bottom-0 left-[4px] w-px" />
          ) : null}
          <span className="bg-phx-border-2 absolute top-1/2 left-[4px] h-px w-2" />
        </span>
      ) : null}
    </>
  );

  if (row.kind !== 'leaf') {
    const name =
      row.kind === 'chunk' && row.chunkRange
        ? t('detail.chunk', {
            from: formatThousands(row.chunkRange.from),
            to: formatThousands(row.chunkRange.to),
          })
        : row.name;
    return (
      <div
        className="flex min-h-[27px] cursor-pointer items-center gap-[7px] pr-4 pl-3"
        onClick={() => onToggle(row)}
        data-testid="job-tree-group"
      >
        {guides}
        <span className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center">
          <JobStatusIcon state={row.visual} size={row.visual === 'running' ? 14 : 15} />
        </span>
        <span className="text-phx-text min-w-0 shrink truncate text-[13px]">{name}</span>
        <ChevronDown
          size={11}
          className={cn(
            'text-phx-text-2 shrink-0 transition-transform duration-200',
            row.collapsed && '-rotate-90'
          )}
          aria-hidden
        />
        <span className="flex-1" />
        {row.maxParallelism !== null ? (
          <Badge tone="neutral" appearance="soft" className="shrink-0">
            {t('detail.group.maxParallel', { count: row.maxParallelism })}
          </Badge>
        ) : null}
        <JobSegmentedBar segments={row.segments} className="h-[5px] w-[84px] shrink-0" />
        <span className="text-phx-text-muted min-w-[34px] shrink-0 text-right text-[11.5px] tabular-nums">
          {formatThousands(row.doneCount)}/{formatThousands(row.totalCount)}
        </span>
      </div>
    );
  }

  const { step, visual, wait } = row;
  const startedAt = step.startedAt ? Date.parse(step.startedAt) : null;
  const completedAt = step.completedAt ? Date.parse(step.completedAt) : null;
  const showProgress = visual === 'running' && step.progressPercent != null;
  const durationText =
    visual === 'queued'
      ? '—'
      : visual === 'running'
        ? showProgress
          ? `${step.progressPercent} %`
          : startedAt !== null
            ? formatDuration(now - startedAt)
            : '—'
        : startedAt !== null && completedAt !== null
          ? formatDuration(completedAt - startedAt)
          : '—';

  return (
    <div className="flex min-h-[27px] items-center gap-[7px] pr-4 pl-3" data-testid="job-tree-leaf">
      {guides}
      <span className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center">
        <JobStatusIcon state={visual} size={visual === 'running' ? 14 : 15} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-px py-0.5">
        <span className="text-phx-text truncate text-[13px]" title={step.name}>
          {step.name}
        </span>
        {wait ? (
          <span className="text-phx-text-faint truncate text-[11px]">
            {wait.kind === 'slot'
              ? t('detail.dep.slot')
              : `${t('detail.dep.waitingFor', { names: wait.names.join(' · ') })}${
                  wait.more > 0 ? ` ${t('detail.dep.more', { count: wait.more })}` : ''
                }`}
          </span>
        ) : null}
        {visual === 'error' && step.error ? (
          <span className="text-phx-danger text-[11px] leading-[1.4]">{step.error}</span>
        ) : null}
      </span>
      {showProgress ? (
        <span className="bg-phx-surface-2 h-1 w-14 shrink-0 overflow-hidden rounded-sm">
          <span
            className="bg-phx-info block h-full transition-[width] duration-150"
            style={{ width: `${step.progressPercent}%` }}
          />
        </span>
      ) : null}
      {visual === 'error' ? (
        <Button
          size="sm"
          className="shrink-0"
          iconLeft="retry"
          loading={retrying}
          disabled={retrying}
          onClick={() => {
            setRetrying(true);
            // Výsledek přijde SSE eventem (step-changed → Blocked); tlačítko jen krátce blokujeme.
            retryJobStep(runId, step.id)
              .catch(() => undefined)
              .finally(() => setRetrying(false));
          }}
        >
          {t('detail.retry')}
        </Button>
      ) : null}
      <span className="text-phx-text-muted min-w-[44px] shrink-0 text-right text-[11.5px] tabular-nums">
        {durationText}
      </span>
    </div>
  );
}
